import { Field } from '@headlessui/react';
import React, { FC } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import { Checkbox } from '../../../../../../components/checkbox/index';
import { Tooltip } from '../../../../../../components/tooltip/index';
import { ScenarioHooks } from '../../../../../../hooks/index';
import { sharePlanTypeTitle } from '../../../../../../types/pool-plans.types';
import { ScenarioType } from '../../../../../../types/scenario-modeling.types';
import { VestingTask } from '../../../../../../types/vesting-tasks.types';
import { toNumber } from '../../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { FundraisingFormSchema } from '../../../fundraising-validation';
import { useGrantStore } from '../grant.store';

export type GrantItemProps = {
  control: Control<FundraisingFormSchema>;
  isChecked: boolean;
  item: VestingTask;
  scenarioId: string;
  scenarioType: ScenarioType;
};

export const GrantItem: FC<GrantItemProps> = ({
  isChecked,
  item,
  control,
  scenarioId,
  scenarioType,
}) => {
  const { grantItem, vestedSharesCount, id, exercisedCount } = item;
  const { grants } = useWatch<FundraisingFormSchema>({ control });
  const currentGrant = grants?.grantItems?.find((grant) => grant.vestingTaskId === id);
  const { scheduleUpdate } = useScenarioCapTableStore();
  const { decreaseSelectedCount, increaseSelectedCount } = useGrantStore();
  const { createScenarioGrant, isPending: isCreatePending } =
    ScenarioHooks.useCreateScenarioGrant();
  const { deleteScenarioGrant, isPending: isDeletePending } =
    ScenarioHooks.useDeleteScenarioGrant();

  const { pointerBlockedStyles } = useScenarioStore();

  const handleSelect = () => {
    scheduleUpdate(scenarioId);
    increaseSelectedCount(scenarioId);
    createScenarioGrant({
      scenarioId,
      data: {
        vestingTaskId: id,
        issuedShares: toNumber(vestedSharesCount) || 0,
      },
    });
  };

  const handleRemove = () => {
    scheduleUpdate(scenarioId);
    if (currentGrant?.id) {
      decreaseSelectedCount(scenarioId);
      deleteScenarioGrant({ scenarioId, grantId: currentGrant.id });
    }
  };

  return (
    <>
      <div
        className={twMerge(
          'flex items-center justify-center',
          vestedSharesCount > 0 ? 'cursor-pointer' : 'cursor-default',
          pointerBlockedStyles,
        )}
      >
        <Field className="flex items-center">
          <Checkbox
            checked={isChecked}
            // The checkbox is controlled by form state that refreshes only on the `grants-updated`
            // socket refetch — block clicks while a mutation is in flight so a double-click cannot
            // fire a second create/delete against the not-yet-refreshed state.
            disabled={vestedSharesCount <= 0 || isCreatePending || isDeletePending}
            id={id}
            onChange={(checked) => {
              checked ? handleSelect() : handleRemove();
            }}
          />
        </Field>
      </div>
      <div className="px-2 py-[26px]">
        <Tooltip
          childrenWrapperClassName="max-w-[300px] pt-[2px]"
          content={grantItem?.stakeholder?.fullName}
        >
          <span className="w-full truncate text-nowrap text-sm font-[450] text-gray-700">
            {grantItem?.stakeholder?.fullName}
          </span>
        </Tooltip>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] uppercase text-gray-700">
          {grantItem?.id.split('-')[0]}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {grantItem?.plan?.type && (
            <AppFormattedMessage id={sharePlanTypeTitle[grantItem?.plan?.type]} />
          )}
        </span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {grantItem?.numbersOfGrants?.toLocaleString('en-US')}
        </span>
      </div>
      {scenarioType === ScenarioType.LIQUIDITY && (
        <div className="px-2 py-[26px]">
          <span className="w-full text-sm font-[450] text-gray-700">
            {exercisedCount?.toLocaleString('en-US')}
          </span>
        </div>
      )}
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {vestedSharesCount?.toLocaleString('en-US')}
        </span>
      </div>

      <div className="px-2 py-[26px]">
        <Tooltip content={grantItem?.plan?.pool?.shareClass?.name}>
          <span className="w-full text-sm font-[450] text-gray-700">
            {grantItem?.plan?.pool?.shareClass?.name}
          </span>
        </Tooltip>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">1x</span>
      </div>
      <div className="px-2 py-[26px]">
        <span className="w-full text-sm font-[450] text-gray-700">
          {vestedSharesCount?.toLocaleString('en-US')}
        </span>
      </div>
    </>
  );
};
